/**
 * Shadow do Workflow do Volto. Duas mudanças:
 *
 * 1. a troca de estado deixa de anunciar sucesso incondicional (abaixo);
 * 2. fluxo editorial de notícias: as transições de `COMENTARIO_DA_TRANSICAO`
 *    abrem um modal pedindo o comentário (obrigatório em devolver/revogar),
 *    que vai para o histórico e para a notificação de quem age em seguida;
 *    depois de toda transição o contador do sino é atualizado na hora.
 *
 * O core dispara `toast.success` na mesma linha do `dispatch`, sem olhar o
 * resultado. Com a validação de campos essenciais do backend (OS 0007/2026,
 * Cap. IV, III), o editor recebia um "Estado atualizado" verde enquanto o
 * conteúdo continuava privado, sem nenhuma pista do que faltava.
 *
 * Nesta versão do Volto a promise do `dispatch` REJEITA em resposta 4xx/5xx
 * (o middleware dispara a ação `_FAIL`, mas devolve a promise original do
 * superagent). O erro então precisa de DOIS handlers: inspeção da ação
 * resolvida e rejeição — os dois roteirados por `transitionErrorMessage`.
 *
 * Quando a recusa vem do guard de campos essenciais, o toast com a lista
 * "Pendentes:" é seguido pela navegação até o formulário de edição, onde o
 * primeiro campo faltante é revelado e destacado.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import uniqBy from 'lodash/uniqBy';
import { toast } from 'react-toastify';
import { defineMessages, useIntl } from 'react-intl';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import Toast from '@plone/volto/components/manage/Toast/Toast';
import { FormFieldWrapper } from '@plone/volto/components/manage/Widgets';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import {
  getWorkflowOptions,
  getCurrentStateMapping,
} from '@plone/volto/helpers/Workflows/Workflows';
import { injectLazyLibs } from '@plone/volto/helpers/Loadable/Loadable';

import { getContent } from '@plone/volto/actions/content/content';
import { getWorkflow } from '@plone/volto/actions/workflow/workflow';
import {
  getMpfNotificacoesContagem,
  transitionWorkflowComComentario,
} from 'volto-ministerio-publico-federal/actions';
import {
  COMENTARIO_DA_TRANSICAO,
  idDaTransicao,
} from 'volto-ministerio-publico-federal/helpers/Notificacoes';
import {
  MPFButton,
  MPFModal,
} from 'volto-ministerio-publico-federal/components/UI';
import {
  transitionErrorMessage,
  TRANSITION_FAILURE_TITLE,
} from 'volto-ministerio-publico-federal/helpers/Workflow/transitionError';
import {
  camposPendentesDaMensagem,
  rolaAteCamposPendentes,
} from 'volto-ministerio-publico-federal/helpers/Workflow/camposPendentes';
import downSVG from '@plone/volto/icons/down-key.svg';
import upSVG from '@plone/volto/icons/up-key.svg';
import checkSVG from '@plone/volto/icons/check.svg';

const messages = defineMessages({
  messageUpdated: {
    id: 'Workflow updated.',
    defaultMessage: 'Workflow updated.',
  },
  messageNoWorkflow: {
    id: 'No workflow',
    defaultMessage: 'No workflow',
  },
  state: {
    id: 'State',
    defaultMessage: 'State',
  },
});

const SingleValue = injectLazyLibs('reactSelect')(({ children, ...props }) => {
  const stateDecorator = {
    marginRight: '10px',
    display: 'inline-block',
    backgroundColor: props.selectProps.value.color || null,
    content: ' ',
    height: '10px',
    width: '10px',
    borderRadius: '50%',
  };
  const { SingleValue } = props.reactSelect.components;
  return (
    <SingleValue {...props}>
      <span style={stateDecorator} />
      {children}
    </SingleValue>
  );
});

const Option = injectLazyLibs('reactSelect')((props) => {
  const stateDecorator = {
    marginRight: '10px',
    display: 'inline-block',
    backgroundColor:
      props.selectProps.value.value === props.data.value
        ? props.selectProps.value.color
        : null,
    content: ' ',
    height: '10px',
    width: '10px',
    borderRadius: '50%',
    border:
      props.selectProps.value.value !== props.data.value
        ? `1px solid ${props.data.color}`
        : null,
  };

  const { Option } = props['reactSelect'].components;
  return (
    <Option {...props}>
      <span style={stateDecorator} />
      <div style={{ marginRight: 'auto' }}>{props.label}</div>
      {props.isFocused && !props.isSelected && (
        <Icon name={checkSVG} size="18px" color="#b8c6c8" />
      )}
      {props.isSelected && <Icon name={checkSVG} size="18px" color="#007bc1" />}
    </Option>
  );
});

const DropdownIndicator = injectLazyLibs('reactSelect')((props) => {
  const { DropdownIndicator } = props.reactSelect.components;
  return (
    <DropdownIndicator {...props} data-testid="workflow-select-dropdown">
      {props.selectProps.menuIsOpen ? (
        <Icon name={upSVG} size="24px" color="#007bc1" />
      ) : (
        <Icon name={downSVG} size="24px" color="#007bc1" />
      )}
    </DropdownIndicator>
  );
});

const selectTheme = (theme) => ({
  ...theme,
  borderRadius: 0,
  colors: {
    ...theme.colors,
    primary25: 'hotpink',
    primary: '#b8c6c8',
  },
});

const customSelectStyles = {
  control: (styles, state) => ({
    ...styles,
    border: 'none',
    borderBottom: '2px solid #b8c6c8',
    boxShadow: 'none',
    borderBottomStyle: state.menuIsOpen ? 'dotted' : 'solid',
  }),
  menu: (styles, state) => ({
    ...styles,
    top: null,
    marginTop: 0,
    boxShadow: 'none',
    borderBottom: '2px solid #b8c6c8',
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    width: null,
  }),
  valueContainer: (styles) => ({
    ...styles,
    padding: 0,
  }),
  option: (styles, state) => ({
    ...styles,
    backgroundColor: null,
    minHeight: '50px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 12px',
    color: state.isSelected
      ? '#007bc1'
      : state.isFocused
        ? '#4a4a4a'
        : 'inherit',
    ':active': {
      backgroundColor: null,
    },
    span: {
      flex: '0 0 auto',
    },
    svg: {
      flex: '0 0 auto',
    },
  }),
};

function useWorkflow() {
  const history = useSelector((state) => state.workflow.history, shallowEqual);
  const transitions = useSelector(
    (state) => state.workflow.transitions,
    shallowEqual,
  );
  const loaded = useSelector((state) => state.workflow.transition.loaded);
  const currentStateValue = useSelector(
    (state) => getCurrentStateMapping(state.workflow.currentState),
    shallowEqual,
  );

  return { loaded, history, transitions, currentStateValue };
}

const Workflow = (props) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { loaded, transitions, currentStateValue } = useWorkflow();
  const content = useSelector((state) => state.content?.data, shallowEqual);
  const { pathname } = props;
  // Transição à espera do comentário (fluxo editorial de notícias).
  const [pendente, setPendente] = useState(null);
  const [comentario, setComentario] = useState('');

  useEffect(() => {
    dispatch(getWorkflow(pathname));
    dispatch(getContent(pathname));
  }, [dispatch, pathname, loaded]);

  const executar = (selectedOption, comment = '') => {
    const notificaFalha = (erro) => {
      const message = transitionErrorMessage(erro);
      toast.error(
        <Toast error title={TRANSITION_FAILURE_TITLE} content={message} />,
      );
      rolaAteCamposPendentes(
        props.pathname,
        camposPendentesDaMensagem(message),
        (url) => history.push(url),
      );
    };

    dispatch(
      transitionWorkflowComComentario(
        flattenToAppURL(selectedOption.url),
        comment,
      ),
    ).then(
      (action) => {
        const message = transitionErrorMessage(action);
        if (message) {
          notificaFalha(action);
          return;
        }
        // A transição pode ter gerado aviso para quem está logado (ex.: o
        // autor que também é editor): o sino não espera o próximo polling.
        dispatch(getMpfNotificacoesContagem()).catch(() => {});
        toast.success(
          <Toast
            success
            title={intl.formatMessage(messages.messageUpdated)}
            content=""
          />,
        );
      },
      (error) => notificaFalha(error),
    );
  };

  const transition = (selectedOption) => {
    if (!selectedOption?.url) return; // linha do estado atual não é transição
    if (COMENTARIO_DA_TRANSICAO[idDaTransicao(selectedOption.url)]) {
      setComentario('');
      setPendente(selectedOption);
      return;
    }
    executar(selectedOption);
  };

  const configComentario = pendente
    ? COMENTARIO_DA_TRANSICAO[idDaTransicao(pendente.url)]
    : null;
  const comentarioInvalido =
    configComentario?.obrigatorio && !comentario.trim();

  const confirmar = () => {
    if (!pendente || comentarioInvalido) return;
    executar(pendente, comentario.trim());
    setPendente(null);
  };

  const { Placeholder } = props.reactSelect.components;
  const Select = props.reactSelect.default;

  return (
    <FormFieldWrapper
      id="state-select"
      title={intl.formatMessage(messages.state)}
      intl={intl}
      {...props}
    >
      <Select
        name="state-select"
        className="react-select-container"
        classNamePrefix="react-select"
        isDisabled={!content.review_state || transitions.length === 0}
        options={uniqBy(
          transitions.map((transition) => getWorkflowOptions(transition)),
          'label',
        ).concat(currentStateValue)}
        styles={customSelectStyles}
        theme={selectTheme}
        components={{
          DropdownIndicator,
          Placeholder,
          Option,
          SingleValue,
        }}
        onChange={transition}
        value={
          content.review_state
            ? currentStateValue
            : {
                label: intl.formatMessage(messages.messageNoWorkflow),
                value: 'noworkflow',
              }
        }
        isSearchable={false}
      />
      <MPFModal
        open={!!pendente}
        onClose={() => setPendente(null)}
        title={configComentario?.titulo || ''}
        size="sm"
        footer={
          <div className="mpf-workflow-comentario__acoes">
            <MPFButton
              variant="outline"
              size="m"
              onClick={() => setPendente(null)}
            >
              Cancelar
            </MPFButton>
            <MPFButton
              size="m"
              disabled={comentarioInvalido}
              onClick={confirmar}
            >
              Confirmar
            </MPFButton>
          </div>
        }
      >
        <label
          className="mpf-workflow-comentario__rotulo"
          htmlFor="mpf-workflow-comentario"
        >
          Comentário{configComentario?.obrigatorio ? ' (obrigatório)' : ''}
        </label>
        <textarea
          id="mpf-workflow-comentario"
          className="mpf-workflow-comentario__campo"
          rows={5}
          value={comentario}
          required={!!configComentario?.obrigatorio}
          aria-describedby="mpf-workflow-comentario-dica"
          onChange={(e) => setComentario(e.target.value)}
        />
        <p
          id="mpf-workflow-comentario-dica"
          className="mpf-workflow-comentario__dica"
        >
          {configComentario?.dica} O comentário é enviado na notificação.
        </p>
      </MPFModal>
    </FormFieldWrapper>
  );
};

Workflow.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default compose(injectLazyLibs(['reactSelect']))(Workflow);
